import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { FilterOption } from '@/components/UI/Table/TableComponent.model';
import { errorToast, infoToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import type { APITableAsyncExportCallback, AsyncExportRoute } from '@/model/AsyncExport.model';
import type { ID } from '@/model/Common.model';
import type { Study } from '@/model/Study.model';
import { ApiService, ResponseType } from '@/support/ApiService';
import { buildAsyncExportFilterStrings } from '@/support/asyncExport';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { useFetchEntity } from '@/support/Hooks/fetch';
import Http from '@/support/http';
import { api as apiRoutes } from '@/support/route';
import { featuresSelector } from '@/support/Selectors';
import { modalAction } from '@/utils/modal';
import { createSelector } from '@reduxjs/toolkit';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { columns, formatFilterOptions, type ObservationWithRelations } from './Observations.utils';
import * as Auth from '@/support/auth.ts';
import { type ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList.tsx';

const featureSelector = createSelector([featuresSelector], (features) => ({
  asyncExportsEnabled: features.async_exports === true,
}));

interface FilterInput {
  value: string;
  options?: Array<{ id: ID; name: string }>;
}

const Show: FC = () => {
  const { id: studyId } = useParams<{ id: string }>();
  const { entity: study, entityLoading: studyLoading } = useFetchEntity<Study>({
    entityType: 'study',
    params: { id: studyId },
  });

  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [errors, setErrors] = useState<Error | null>(null);
  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);
  const isWriteUser = _notNil(study) ? Auth.isWriteUserForStudy(study) : false;
  const { asyncExportsEnabled } = useSelector(featureSelector);

  const { invoke: deleteObservations } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyApiId}/observations/remove',
    invokeOnInit: false,
  });

  const { invoke: triggerExportGenerate } = useLegacyApiHook({
    method: 'POST',
    apiRoute: 'exports.generate',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'exports-generate' } },
  });

  const getFilterOptions = () => {
    if (_notNil(study)) {
      Http.get<Array<FilterInput>>(
        apiRoutes('studies.observations.filter.get', {
          id: study.id,
        })
      )
        .then(({ data }) => setFilterOptions(formatFilterOptions(data)))
        .catch((error: Error) => {
          setErrors(error);
        });
    }
  };

  useEffect(() => {
    if (_notNil(study)) {
      getFilterOptions();
    }
  }, [study]);

  const memoizedColumns = useMemo(() => (_notNil(study) ? columns(study.id) : []), [study?.id]);

  const exportRoutes = useMemo(() => {
    if (!_notNil(study)) {
      return [];
    }
    if (asyncExportsEnabled) {
      const asyncRoutes: Array<AsyncExportRoute> = [
        {
          endpoint: 'POST /api/v1/studies/{studyApiId}/exports/observations',
          params: { studyApiId: study.api_id },
          label: 'All observations',
          noFilters: true,
        },
        {
          endpoint: 'POST /api/v1/studies/{studyApiId}/exports/observations',
          params: { studyApiId: study.api_id },
          label: 'Current filter selection',
        },
      ];
      return asyncRoutes;
    }
    return [
      {
        label: 'All observations',
        url: 'studies.observations.export',
        params: { id: study.id },
        noFilters: true,
      },
      {
        label: 'Current filter selection',
        url: 'studies.observations.export',
        params: { id: study.id },
      },
    ];
  }, [study, asyncExportsEnabled]);

  const handleAsyncExport = useCallback<APITableAsyncExportCallback>(
    async ({ route, view }) => {
      const noFilters = route.noFilters === true;
      const filterStrings = buildAsyncExportFilterStrings({ route, view });
      const queryText = view.query?.trim();

      const response = await ApiService.call({
        endpoint: 'POST /api/v1/studies/{studyApiId}/exports/observations',
        path: route.params,
        body: {
          sort: noFilters ? null : (view.sort?.id ?? null),
          order: noFilters || !_notNil(view.sort) ? null : view.sort.desc === true ? 'desc' : 'asc',
          filters: filterStrings,
          filter_type: noFilters ? null : view.match_all_filters === false ? 'or' : 'and',
          query: noFilters || !queryText ? null : queryText,
          per_page: noFilters ? null : (view.page_size ?? null),
          page: null,
          columns: null,
        },
        options: { onError: { toast: false, throw: false, slug: 'observations-async-export' } },
      });

      if (response.type !== ResponseType.Success) {
        errorToast('Could not queue export');
        return;
      }
      const exportApiId = response.body?.api_id;
      if (_notNil(exportApiId)) {
        triggerExportGenerate({ path: { exportApiId } }).catch(() => {
          // Fire-and-forget: failure is persisted on the row and visible in the Exports tab.
        });
      }
      infoToast('Export queued — see the Exports tab when ready.');
    },
    [triggerExportGenerate]
  );

  const handleBulkDelete = async (
    observationApiIds: Array<string>,
    reasonForChange: string,
    refetchTableData: () => Promise<void>
  ) => {
    if (_notNil(study)) {
      const response = await deleteObservations({
        path: { studyApiId: study.api_id },
        body: {
          observation_api_ids: observationApiIds,
          reason_for_change: reasonForChange,
        },
      });

      if (response.type === ResponseType.Success) {
        successToast('Successfully deleted observations');
        closeModal();
        await refetchTableData();
      } else {
        errorToast('Failed to delete observations');
      }
    }
  };

  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: UseTableProps<ObservationWithRelations>): Array<{ name: string; action: () => void; className?: string }> => {
    const observations = selectedFlatRows.map((row) => row.original);
    const observationApiIds = observations.map((obs) => obs.api_id);

    if (!isWriteUser) {
      return [];
    }

    const actions: Array<ActionProps> = [
      {
        name: 'Delete',
        className: 'red',
        action: () =>
          openModal('CONFIRM_DELETE_OBSERVATION', {
            onClick: (reasonForChange: string) =>
              handleBulkDelete(observationApiIds, reasonForChange, refetchTableData),
            closeModal,
          }),
      },
    ];
    return actions;
  };

  if (studyLoading) {
    return null;
  }

  return (
    <>
      {_notNil(errors) ? (
        <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={errors} />
      ) : (
        <div className="ph4 pt3">
          {_notNil(study) && (
            <APITable
              columns={memoizedColumns}
              apiInfo={{
                type: 'legacyInternalApi',
                route: apiRoutes('studies.observations.show', {
                  id: study.id,
                }),
              }}
              reduxTableName="observations"
              includeParam="study_group"
              pageSizes={[50, 100, 200, 500]}
              showViewSelector
              defaultPageSize={50}
              filterOptions={filterOptions}
              exportRoutes={exportRoutes}
              onAsyncExport={asyncExportsEnabled ? handleAsyncExport : undefined}
              searchPlaceholderText="Search by animal name or observation"
              bulkActions={bulkActions}
              testIdPrefix="observations"
            />
          )}
        </div>
      )}
    </>
  );
};

export default Show;
